import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { DataCatalogueEntry, DatasetDefinitions } from "data/models";
import { fetchCatalogueList, fetchDefinitions } from "data/queries";
import _ from "lodash";
import { useState } from "react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { CatalogueCard } from "./catalogue-card";

// Reverse mapping for NATIONAL X back to backend keys
const REVERSE_PRODUCT_MAPPING: Record<string, string> = {
  "NATIONAL PLANNING": "GEOSCAPE PLANNING",
  "NATIONAL PLANNING INSIGHTS": "GEOSCAPE PLANNING INSIGHTS",
  "NATIONAL BUILDINGS": "GEOSCAPE BUILDINGS",
  "NATIONAL CADASTRE": "GEOSCAPE CADASTRE",
  "NATIONAL PROPERTY": "GEOSCAPE PROPERTY",
  "NATIONAL TREES": "GEOSCAPE TREES",
  "NATIONAL ROADS": "GEOSCAPE ROADS",
  "NATIONAL AIRPORTS": "GEOSCAPE AIRPORTS",
  "NATIONAL RAIL": "GEOSCAPE RAIL",
  "NATIONAL LOCALITIES": "GEOSCAPE LOCALITIES",
  "NATIONAL LOCAL GOVERNMENT AREAS": "GEOSCAPE LOCAL GOVERNMENT AREAS",
  "NATIONAL WARDS": "GEOSCAPE WARDS",
  "NATIONAL SOLAR": "GEOSCAPE SOLAR",
  "NATIONAL SURFACE COVER": "GEOSCAPE SURFACE COVER",
  "NATIONAL GREENSPACE": "GEOSCAPE GREENSPACE",
  "NATIONAL HYDROLOGY": "GEOSCAPE HYDROLOGY",
  "NATIONAL ELECTORAL BOUNDARIES": "ELECTORAL BOUNDARIES",
  "NATIONAL POSTCODE BOUNDARIES": "POSTCODE BOUNDARIES",
};

export const CataloguePage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);
  const [query, setQuery] = useState("");

  const catalogueListQuery = useQuery({
      queryKey: [QueryKeys.catalogueListKey, identityId, query],
      queryFn: () => fetchCatalogueList(query),
    });

  const datasetDefinitions: UseQueryResult<DatasetDefinitions> = useQuery({
      queryKey: [QueryKeys.definitionsKey],
      queryFn: () => fetchDefinitions(),
    });

  const isLoading: boolean =
    catalogueListQuery.isPending || datasetDefinitions.isPending;
  const isError: boolean =
    catalogueListQuery.isError || datasetDefinitions.isError;
  const isSuccess: boolean =
    catalogueListQuery.isSuccess && datasetDefinitions.isSuccess;

  const skeletonArray: number[] = _.range(1, 18);
  return (
    <Stack direction="column" gap={2}>
      <Card>
        <CardHeader
          sx={{ alignSelf: "flex-start" }}
          title="Data Catalogue"
        ></CardHeader>
        <CardContent>
          <TextField
            id="query"
            name="query"
            label="Search"
            type="text"
            value={query}
            onChange={(e: any) => {
              setQuery(e.target.value);
            }}
            disabled={isError}
            helperText={
              "Search for keywords to narrow the list of data products"
            }
            variant={"outlined"}
            fullWidth
          />
        </CardContent>
      </Card>
      <Grid container spacing={2}>
        <ReactQueryErrorWrapper
          queries={[catalogueListQuery, datasetDefinitions]}
          mutations={[]}
        />

        {isLoading &&
          skeletonArray.map((i) => {
            return (
              <Grid item md={12} lg={6} key={i}>
                <Skeleton variant="rectangular" height="250px" />
              </Grid>
            );
          })}

        {isSuccess &&
          catalogueListQuery.data
            .sort((a: DataCatalogueEntry, b: DataCatalogueEntry) =>
              a.id.localeCompare(b.id)
            )
            .map((catalogueItem: DataCatalogueEntry, index: number) => {
              return (
                <Grid item md={12} lg={6} key={`${catalogueItem.id}-${index}`}>
                  <CatalogueCard
                    productId={catalogueItem.id}
                    displayProduct={catalogueItem.displayProduct}
                    totalFeatureCount={catalogueItem.metadata.totalFeatureCount}
                    shortDescription={catalogueItem.shortDescription}
                    updatedDate={
                      // Lookup metadata using backend keys
                      (() => {
                        // Reverse map NATIONAL X back to backend key for metadata lookup
                        let lookupKey = REVERSE_PRODUCT_MAPPING[catalogueItem.id] || catalogueItem.id;

                        // Special case: Planning Insights needs BASE suffix
                        if (lookupKey === "GEOSCAPE PLANNING INSIGHTS") {
                          lookupKey = "GEOSCAPE PLANNING INSIGHTS - BASE";
                        }

                        return datasetDefinitions.data?.metadata[lookupKey]?.latestRelease;
                      })()
                    }
                    image={`data:image/png;base64,${
                      isDark
                        ? catalogueItem.imageDark
                        : catalogueItem.imageLight
                    }`}
                    dataType={catalogueItem.metadata.dataType}
                  ></CatalogueCard>
                </Grid>
              );
            })}
      </Grid>
    </Stack>
  );
};

export const viewDatasetInExplorer = (dataset: string, history: any) => {
  // Reverse map NATIONAL back to backend key for compatibility
  let datasetId = REVERSE_PRODUCT_MAPPING[dataset] || dataset;

  // Special case: Planning Insights needs BASE suffix
  if (datasetId === "GEOSCAPE PLANNING INSIGHTS") {
    datasetId = "GEOSCAPE PLANNING INSIGHTS - BASE";
  }

  sessionStorage.setItem("catalogueToExplorer", datasetId);
  history.push(Routes.dataExplorer);
};
